import { type ThreeEvent } from "@react-three/fiber";
import { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { getTileHeight } from "../../shared/room-layout";
import type { RoomAsset, RoomLayoutRuntime } from "../../shared/types";
import {
  FRAME_ROTATION_NORTH,
  clampFrameDimensions,
  frameDirectionFromRotation,
  frameMaxTileHeightForLayout,
  frameRotationFromDirection,
  frameSlotToWorldTransform,
} from "../../shared/frame-placement";

const FRAME_DEPTH = 0.028;
const BACKBOARD_DEPTH = 0.008;
const FRAME_IDLE_COLOR = "#1f2126";
const FRAME_IDLE_EMISSIVE = "#07090d";
const DEFAULT_ANIMATED_FRAME_DELAY_MS = 83;

type FrameMediaKind = "video" | "animated-image" | "image";

interface ProbedFrameMedia {
  kind: FrameMediaKind;
  sourceUrl: string;
  mime?: string;
  buffer?: ArrayBuffer;
  revokeSourceUrl?: boolean;
}

function normalizeMime(value?: string | null): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return undefined;
  const [mime] = trimmed.split(";");
  return mime?.trim() || undefined;
}

function isThumb288Url(url: string): boolean {
  return /\/thumb288(?:$|\?)/i.test(url);
}

function isVideoMime(mime?: string): boolean {
  return typeof mime === "string" && mime.toLowerCase().startsWith("video/");
}

function isVideoMedia(url: string, mime?: string): boolean {
  if (isVideoMime(mime)) {
    return true;
  }
  return /\.webm(?:$|\?)/i.test(url);
}

function isAnimatedImageMime(mime?: string): boolean {
  if (typeof mime !== "string") return false;
  const lower = mime.toLowerCase();
  return lower === "image/gif" || lower === "image/webp" || lower === "image/apng";
}

function hasFourCc(bytes: Uint8Array, offset: number, value: string): boolean {
  if (bytes.length < offset + 4 || value.length !== 4) return false;
  return (
    bytes[offset] === value.charCodeAt(0) &&
    bytes[offset + 1] === value.charCodeAt(1) &&
    bytes[offset + 2] === value.charCodeAt(2) &&
    bytes[offset + 3] === value.charCodeAt(3)
  );
}

function sniffMimeFromBytes(bytes: Uint8Array): string | undefined {
  if (bytes.length >= 4) {
    if (
      bytes[0] === 0x1a &&
      bytes[1] === 0x45 &&
      bytes[2] === 0xdf &&
      bytes[3] === 0xa3
    ) {
      return "video/webm";
    }
  }
  if (bytes.length >= 12 && hasFourCc(bytes, 0, "RIFF") && hasFourCc(bytes, 8, "WEBP")) {
    return "image/webp";
  }
  if (
    bytes.length >= 6 &&
    hasFourCc(bytes, 0, "GIF8") &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61
  ) {
    return "image/gif";
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "image/png";
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  return undefined;
}

function isAnimatedWebP(bytes: Uint8Array): boolean {
  if (!(bytes.length >= 12 && hasFourCc(bytes, 0, "RIFF") && hasFourCc(bytes, 8, "WEBP"))) {
    return false;
  }

  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const chunkTag = String.fromCharCode(
      bytes[offset],
      bytes[offset + 1],
      bytes[offset + 2],
      bytes[offset + 3]
    );
    const chunkSize =
      bytes[offset + 4] |
      (bytes[offset + 5] << 8) |
      (bytes[offset + 6] << 16) |
      (bytes[offset + 7] << 24);
    const payloadOffset = offset + 8;
    if (chunkTag === "ANIM" || chunkTag === "ANMF") {
      return true;
    }
    if (chunkTag === "VP8X" && payloadOffset < bytes.length) {
      const featureFlags = bytes[payloadOffset];
      if ((featureFlags & 0x02) !== 0) {
        return true;
      }
    }
    if (chunkSize < 0) {
      return false;
    }
    const paddedChunkSize = chunkSize + (chunkSize % 2);
    offset = payloadOffset + paddedChunkSize;
  }
  return false;
}

function mediaKindFromMime(mime?: string, bytes?: Uint8Array): FrameMediaKind {
  if (isVideoMime(mime)) {
    return "video";
  }
  if (mime === "image/webp" && bytes) {
    return isAnimatedWebP(bytes) ? "animated-image" : "image";
  }
  if (isAnimatedImageMime(mime)) {
    return "animated-image";
  }
  return "image";
}

function frameDelayMs(durationUs?: number | null): number {
  if (!Number.isFinite(durationUs) || (durationUs as number) <= 0) {
    return DEFAULT_ANIMATED_FRAME_DELAY_MS;
  }
  return Math.max(16, Math.min(400, Math.round((durationUs as number) / 1000)));
}

function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

async function probeFrameMedia(
  url: string,
  mimeHint?: string,
  signal?: AbortSignal
): Promise<ProbedFrameMedia> {
  const normalizedHint = normalizeMime(mimeHint);
  const shouldProbe =
    isThumb288Url(url) || !normalizedHint || normalizedHint === "application/octet-stream";
  if (!shouldProbe) {
    return {
      kind: mediaKindFromMime(normalizedHint),
      sourceUrl: url,
      mime: normalizedHint,
    };
  }

  const response = await fetch(url, {
    method: "GET",
    credentials: "omit",
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to load media ${response.status}`);
  }

  const headerMime = normalizeMime(response.headers.get("content-type"));
  const blob = await response.blob();
  const blobMime = normalizeMime(blob.type);
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const sniffedMime = sniffMimeFromBytes(bytes);
  const resolvedMime = blobMime ?? headerMime ?? sniffedMime ?? normalizedHint;
  const sourceUrl = URL.createObjectURL(blob);

  return {
    kind: mediaKindFromMime(resolvedMime, bytes),
    sourceUrl,
    mime: resolvedMime,
    buffer,
    revokeSourceUrl: true,
  };
}

function useFrameMediaTexture(url: string, mime?: string): THREE.Texture | null {
  const [texture, setTexture] = useState<THREE.Texture | null>(null);

  useEffect(() => {
    if (!url) {
      setTexture(null);
      return;
    }

    let disposed = false;
    let createdTexture: THREE.Texture | null = null;
    let videoEl: HTMLVideoElement | null = null;
    let imageEl: HTMLImageElement | null = null;
    let mountedImageEl: HTMLImageElement | null = null;
    let canvasEl: HTMLCanvasElement | null = null;
    let canvasCtx: CanvasRenderingContext2D | null = null;
    let canvasTexture: THREE.CanvasTexture | null = null;
    let decoder: { close?: () => void } | null = null;
    let animationFrameId: number | null = null;
    let decodeTimerId: number | null = null;
    let objectUrlToRevoke: string | null = null;
    const abortController = new AbortController();
    setTexture(null);

    const createCanvasTexture = (
      sourceWidth: number,
      sourceHeight: number
    ): THREE.CanvasTexture | null => {
      const width = Math.max(1, Math.floor(sourceWidth));
      const height = Math.max(1, Math.floor(sourceHeight));
      if (!canvasEl) {
        canvasEl = document.createElement("canvas");
      }
      if (canvasEl.width !== width || canvasEl.height !== height) {
        canvasEl.width = width;
        canvasEl.height = height;
      }
      canvasCtx = canvasEl.getContext("2d", { alpha: true });
      if (!canvasCtx) {
        return null;
      }

      if (!canvasTexture) {
        const textureCandidate = new THREE.CanvasTexture(canvasEl);
        textureCandidate.colorSpace = THREE.SRGBColorSpace;
        textureCandidate.anisotropy = 8;
        textureCandidate.magFilter = THREE.LinearFilter;
        textureCandidate.minFilter = THREE.LinearFilter;
        textureCandidate.generateMipmaps = false;
        textureCandidate.needsUpdate = true;
        canvasTexture = textureCandidate;
        createdTexture = textureCandidate;
        if (!disposed) {
          setTexture(textureCandidate);
        }
      }
      return canvasTexture;
    };

    const loadVideoTexture = (sourceUrl: string) => {
      videoEl = document.createElement("video");
      videoEl.src = sourceUrl;
      if (isHttpUrl(sourceUrl)) {
        videoEl.crossOrigin = "anonymous";
      }
      videoEl.muted = true;
      videoEl.loop = true;
      videoEl.playsInline = true;
      videoEl.autoplay = true;
      videoEl.preload = "auto";

      const textureCandidate = new THREE.VideoTexture(videoEl);
      textureCandidate.colorSpace = THREE.SRGBColorSpace;
      textureCandidate.magFilter = THREE.LinearFilter;
      textureCandidate.minFilter = THREE.LinearFilter;
      textureCandidate.generateMipmaps = false;
      textureCandidate.needsUpdate = true;
      createdTexture = textureCandidate;
      if (!disposed) {
        setTexture(textureCandidate);
      }
      void videoEl.play().catch(() => undefined);
    };

    const loadAnimatedImageViaCanvas = (sourceUrl: string) => {
      imageEl = new Image();
      if (isHttpUrl(sourceUrl)) {
        imageEl.crossOrigin = "anonymous";
      }
      imageEl.decoding = "async";
      imageEl.loading = "eager";
      imageEl.style.position = "fixed";
      imageEl.style.left = "-9999px";
      imageEl.style.top = "-9999px";
      imageEl.style.width = "1px";
      imageEl.style.height = "1px";
      imageEl.style.opacity = "0";
      imageEl.style.pointerEvents = "none";
      imageEl.style.zIndex = "-1";
      document.body.appendChild(imageEl);
      mountedImageEl = imageEl;
      imageEl.src = sourceUrl;
      imageEl.onload = () => {
        if (disposed) return;
        const sourceWidth = Math.max(1, imageEl?.naturalWidth || 1);
        const sourceHeight = Math.max(1, imageEl?.naturalHeight || 1);
        const textureCandidate = createCanvasTexture(sourceWidth, sourceHeight);
        if (!canvasCtx || !canvasEl || !textureCandidate) {
          setTexture(null);
          return;
        }

        const tick = () => {
          if (
            disposed ||
            !canvasCtx ||
            !imageEl ||
            !canvasEl ||
            !textureCandidate
          ) {
            return;
          }
          canvasCtx.clearRect(0, 0, canvasEl.width, canvasEl.height);
          try {
            canvasCtx.drawImage(imageEl, 0, 0, canvasEl.width, canvasEl.height);
          } catch {
            return;
          }
          textureCandidate.needsUpdate = true;
          animationFrameId = window.requestAnimationFrame(tick);
        };
        animationFrameId = window.requestAnimationFrame(tick);
      };
      imageEl.onerror = () => {
        if (!disposed) {
          setTexture(null);
        }
      };
    };

    const decodeAnimatedImageTexture = async (
      resolvedMime?: string,
      buffer?: ArrayBuffer
    ): Promise<boolean> => {
      if (!buffer) {
        return false;
      }

      const decoderType = normalizeMime(resolvedMime) ?? "image/webp";
      const decoderCtor = (
        window as Window & {
          ImageDecoder?: {
            new (init: {
              type: string;
              data: ArrayBuffer;
              preferAnimation?: boolean;
            }): {
              decode: (options?: { frameIndex?: number }) => Promise<{
                image: VideoFrame;
              }>;
              close?: () => void;
              tracks?: {
                ready?: Promise<unknown>;
                selectedTrack?: {
                  animated?: boolean;
                  frameCount?: number;
                };
              };
            };
            isTypeSupported?: (type: string) => Promise<boolean>;
          };
        }
      ).ImageDecoder;

      if (!decoderCtor) {
        return false;
      }

      if (typeof decoderCtor.isTypeSupported === "function") {
        const supported = await decoderCtor
          .isTypeSupported(decoderType)
          .catch(() => false);
        if (!supported) {
          return false;
        }
      }

      const liveDecoder = new decoderCtor({
        type: decoderType,
        data: buffer,
        preferAnimation: true,
      });
      decoder = liveDecoder;
      if (liveDecoder.tracks?.ready) {
        await liveDecoder.tracks.ready;
      }
      const selectedTrack = liveDecoder.tracks?.selectedTrack;
      const frameCount =
        typeof selectedTrack?.frameCount === "number" && selectedTrack.frameCount > 0
          ? selectedTrack.frameCount
          : 1;
      const isAnimatedTrack = selectedTrack?.animated === true || frameCount > 1;
      let frameIndex = 0;

      const decodeNext = async (): Promise<void> => {
        if (disposed || !decoder) {
          return;
        }
        const frame = await liveDecoder.decode({ frameIndex }).catch(() => null);
        if (!frame) {
          return;
        }
        const imageFrame = frame.image;
        if (disposed || !decoder) {
          imageFrame.close();
          return;
        }

        const width = Math.max(
          1,
          imageFrame.displayWidth || imageFrame.codedWidth || 1
        );
        const height = Math.max(
          1,
          imageFrame.displayHeight || imageFrame.codedHeight || 1
        );
        const textureCandidate = createCanvasTexture(width, height);
        if (!canvasCtx || !canvasEl || !textureCandidate) {
          imageFrame.close();
          return;
        }

        canvasCtx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        canvasCtx.drawImage(
          imageFrame as unknown as CanvasImageSource,
          0,
          0,
          canvasEl.width,
          canvasEl.height
        );
        textureCandidate.needsUpdate = true;
        const duration = imageFrame.duration;
        imageFrame.close();
        frameIndex = frameCount > 1 ? (frameIndex + 1) % frameCount : 0;

        if (!isAnimatedTrack || disposed || !decoder) {
          return;
        }
        decodeTimerId = window.setTimeout(() => {
          void decodeNext();
        }, frameDelayMs(duration));
      };

      await decodeNext();
      return true;
    };

    const loadStaticTexture = (sourceUrl: string) => {
      const loader = new THREE.TextureLoader();
      if (isHttpUrl(sourceUrl)) {
        loader.setCrossOrigin("anonymous");
      }
      loader.load(
        sourceUrl,
        (loaded) => {
          if (disposed) {
            loaded.dispose();
            return;
          }
          loaded.colorSpace = THREE.SRGBColorSpace;
          loaded.anisotropy = 8;
          loaded.magFilter = THREE.LinearFilter;
          loaded.minFilter = THREE.LinearMipmapLinearFilter;
          loaded.needsUpdate = true;
          createdTexture = loaded;
          setTexture(loaded);
        },
        undefined,
        () => {
          if (!disposed) {
            setTexture(null);
          }
        }
      );
    };

    const loadTexture = async () => {
      let media: ProbedFrameMedia;
      try {
        media = await probeFrameMedia(url, mime, abortController.signal);
      } catch {
        if (abortController.signal.aborted || disposed) {
          return;
        }
        const fallbackMime = normalizeMime(mime);
        media = {
          kind: isVideoMedia(url, fallbackMime)
            ? "video"
            : mediaKindFromMime(fallbackMime),
          sourceUrl: url,
          mime: fallbackMime,
        };
      }

      if (disposed) {
        if (media.revokeSourceUrl) {
          URL.revokeObjectURL(media.sourceUrl);
        }
        return;
      }

      if (media.revokeSourceUrl) {
        objectUrlToRevoke = media.sourceUrl;
      }

      if (media.kind === "video") {
        loadVideoTexture(media.sourceUrl);
        return;
      }

      if (media.kind === "animated-image") {
        const decoded = await decodeAnimatedImageTexture(media.mime, media.buffer);
        if (!decoded && !disposed) {
          loadAnimatedImageViaCanvas(media.sourceUrl);
        }
        return;
      }

      loadStaticTexture(media.sourceUrl);
    };

    void loadTexture();

    return () => {
      disposed = true;
      abortController.abort();
      if (createdTexture) {
        createdTexture.dispose();
      }
      if (videoEl) {
        videoEl.pause();
        videoEl.removeAttribute("src");
        videoEl.load();
      }
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (decodeTimerId != null) {
        window.clearTimeout(decodeTimerId);
      }
      if (decoder?.close) {
        decoder.close();
      }
      if (imageEl) {
        imageEl.src = "";
      }
      if (mountedImageEl?.parentElement) {
        mountedImageEl.parentElement.removeChild(mountedImageEl);
      }
      if (canvasEl) {
        canvasEl.width = 0;
        canvasEl.height = 0;
      }
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
      canvasCtx = null;
    };
  }, [url, mime]);

  return texture;
}

interface Props {
  asset: RoomAsset;
  layout?: RoomLayoutRuntime;
  editMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

export function FrameAsset({
  asset,
  layout,
  editMode,
  selected,
  onSelect,
}: Props) {
  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    if (!editMode || !onSelect) return;
    e.stopPropagation();
    onSelect();
  };

  const frameDims = useMemo(
    () => clampFrameDimensions(asset.frameWidth, asset.frameHeight),
    [asset.frameWidth, asset.frameHeight]
  );
  const frameWidth = frameDims.width;
  const frameHeight = frameDims.height;
  const frameBorder = THREE.MathUtils.clamp(
    Math.min(frameWidth, frameHeight) * 0.08,
    0.045,
    0.11
  );
  const frameInnerWidth = Math.max(0.08, frameWidth - frameBorder * 2);
  const frameInnerHeight = Math.max(0.08, frameHeight - frameBorder * 2);
  const horizontalBarOffset = (frameHeight - frameBorder) / 2;
  const verticalBarOffset = (frameWidth - frameBorder) / 2;
  const backboardZ = -FRAME_DEPTH / 2 + BACKBOARD_DEPTH / 2 + 0.001;
  const imageZ = backboardZ + BACKBOARD_DEPTH / 2 + 0.001;

  const tokenThumbUrl =
    asset.faContract && asset.tokenId
      ? `https://assets.objkt.media/file/assets-003/${asset.faContract}/${asset.tokenId}/thumb288`
      : null;
  const imageUrl = tokenThumbUrl ?? asset.thumbnail ?? asset.url;
  const mediaTexture = useFrameMediaTexture(imageUrl, asset.mediaMime);

  const roundedX = Math.round(asset.x);
  const roundedZ = Math.round(asset.z);
  const normalizedRotation = frameDirectionFromRotation(asset.rotation ?? NaN)
    ? frameRotationFromDirection(
        frameDirectionFromRotation(asset.rotation ?? NaN) ?? "north"
      )
    : FRAME_ROTATION_NORTH;
  const tileHeight = layout ? getTileHeight(layout, roundedX, roundedZ) : 0;
  const maxTileHeight = layout ? frameMaxTileHeightForLayout(layout) : tileHeight;
  const transform = frameSlotToWorldTransform(
    {
      x: asset.x,
      y: asset.y,
      z: asset.z,
      rotation: normalizedRotation,
    },
    tileHeight,
    { maxTileHeight }
  );
  const position: [number, number, number] = [transform.x, transform.y, transform.z];
  const rotationY = transform.rotationY;
  const frameColor = selected ? "#ff922b" : FRAME_IDLE_COLOR;
  const frameEmissive = selected ? "#2a1100" : FRAME_IDLE_EMISSIVE;

  return (
    <group
      position={position}
      rotation={[0, rotationY, 0]}
      onClick={handleClick}
      onPointerOver={
        editMode
          ? (e) => {
              e.stopPropagation();
              document.body.style.cursor = "pointer";
            }
          : undefined
      }
      onPointerOut={
        editMode
          ? () => {
              document.body.style.cursor = "";
            }
          : undefined
      }
    >
      <mesh position={[0, horizontalBarOffset, 0]}>
        <boxGeometry args={[frameWidth, frameBorder, FRAME_DEPTH]} />
        <meshStandardMaterial
          color={frameColor}
          emissive={frameEmissive}
          metalness={0.22}
          roughness={0.58}
        />
      </mesh>
      <mesh position={[0, -horizontalBarOffset, 0]}>
        <boxGeometry args={[frameWidth, frameBorder, FRAME_DEPTH]} />
        <meshStandardMaterial
          color={frameColor}
          emissive={frameEmissive}
          metalness={0.22}
          roughness={0.58}
        />
      </mesh>
      <mesh position={[-verticalBarOffset, 0, 0]}>
        <boxGeometry args={[frameBorder, frameInnerHeight, FRAME_DEPTH]} />
        <meshStandardMaterial
          color={frameColor}
          emissive={frameEmissive}
          metalness={0.22}
          roughness={0.58}
        />
      </mesh>
      <mesh position={[verticalBarOffset, 0, 0]}>
        <boxGeometry args={[frameBorder, frameInnerHeight, FRAME_DEPTH]} />
        <meshStandardMaterial
          color={frameColor}
          emissive={frameEmissive}
          metalness={0.22}
          roughness={0.58}
        />
      </mesh>
      <mesh position={[0, 0, backboardZ]}>
        <boxGeometry
          args={[
            Math.max(0.06, frameInnerWidth - 0.02),
            Math.max(0.06, frameInnerHeight - 0.02),
            BACKBOARD_DEPTH,
          ]}
        />
        <meshStandardMaterial color="#1a1b20" roughness={0.7} metalness={0.05} />
      </mesh>
      <mesh position={[0, 0, imageZ]}>
        <planeGeometry
          args={[
            Math.max(0.06, frameInnerWidth - 0.06),
            Math.max(0.06, frameInnerHeight - 0.06),
          ]}
        />
        {mediaTexture ? (
          <meshBasicMaterial map={mediaTexture} toneMapped={false} />
        ) : (
          <meshStandardMaterial color="#2a2c33" roughness={0.7} metalness={0.05} />
        )}
      </mesh>
      {selected && (
        <mesh>
          <boxGeometry args={[frameWidth + 0.08, frameHeight + 0.08, 0.1]} />
          <meshBasicMaterial
            color="#ff922b"
            transparent
            opacity={0.2}
            depthWrite={false}
          />
        </mesh>
      )}
    </group>
  );
}
